/**
 * Componente CalendarFilters
 * Filtros para el calendario unificado
 * @module components/appointments/CalendarFilters
 */

import React from 'react';
import { UserIcon } from '@heroicons/react/24/outline';
import { DelegationSelector } from './DelegationSelector';
import { useCRMUsers } from '@/hooks/useCRMUsers';
import type { CalendarEventType } from '@/types/calendar.types';

/** Roles que se muestran en el filtro de usuario del calendario */
const CALENDAR_USER_ROLES = ['agent', 'lawyer', 'admin'];

/** Etiquetas legibles para cada rol */
const ROLE_LABELS: Record<string, string> = {
  agent: 'Agente',
  lawyer: 'Abogado',
  admin: 'Admin',
};

interface CalendarFiltersProps {
  selectedDelegation?: string;
  onDelegationChange: (delegationId: string | undefined) => void;
  selectedEventTypes: CalendarEventType[];
  onEventTypesChange: (types: CalendarEventType[]) => void;
  selectedUserId?: string;
  onUserChange: (userId: string | undefined) => void;
  className?: string;
}

export const CalendarFilters: React.FC<CalendarFiltersProps> = ({
  selectedDelegation,
  onDelegationChange,
  selectedEventTypes,
  onEventTypesChange,
  selectedUserId,
  onUserChange,
  className = '',
}) => {
  // Cargar usuarios responsables (agentes, abogados, admins)
  const { users, loading: usersLoading } = useCRMUsers({
    isActive: true,
    onlyResponsibles: true,
  });

  // Filtrar solo roles relevantes y agrupar por rol
  const filteredUsers = users.filter(
    (u) => u.role_name && CALENDAR_USER_ROLES.includes(u.role_name)
  );

  // Agrupar usuarios por rol para el optgroup
  const usersByRole = CALENDAR_USER_ROLES.reduce<
    Record<string, typeof filteredUsers>
  >((acc, role) => {
    acc[role] = filteredUsers.filter((u) => u.role_name === role);
    return acc;
  }, {});

  const handleEventTypeToggle = (type: CalendarEventType) => {
    if (selectedEventTypes.includes(type)) {
      onEventTypesChange(selectedEventTypes.filter((t) => t !== type));
    } else {
      onEventTypesChange([...selectedEventTypes, type]);
    }
  };

  const handleUserChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    onUserChange(val === '' ? undefined : val);
  };

  return (
    <div className={`flex flex-wrap items-center gap-4 ${className}`}>
      {/* Filtro de delegación */}
      <DelegationSelector
        value={selectedDelegation}
        onChange={onDelegationChange}
        label=""
        placeholder="Todas las delegaciones"
        showAllOption={true}
        className="w-64"
      />

      {/* Filtro de usuario/agente */}
      <div className="w-64">
        <div className="relative">
          <select
            id="calendar-user-filter"
            value={selectedUserId || ''}
            onChange={handleUserChange}
            disabled={usersLoading}
            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            <option value="">Todos los usuarios</option>
            {usersLoading && (
              <option value="" disabled>
                Cargando usuarios...
              </option>
            )}
            {CALENDAR_USER_ROLES.map((role) => {
              const roleUsers = usersByRole[role];
              if (!roleUsers || roleUsers.length === 0) return null;
              return (
                <optgroup key={role} label={`── ${ROLE_LABELS[role] || role} ──`}>
                  {roleUsers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name} ({ROLE_LABELS[user.role_name || ''] || user.role_name})
                    </option>
                  ))}
                </optgroup>
              );
            })}
          </select>
          <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {/* Filtro de tipos de eventos */}
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-gray-700">Mostrar:</span>
        <label className="inline-flex items-center gap-1.5 cursor-pointer">
          <input
            type="checkbox"
            checked={selectedEventTypes.includes('appointment')}
            onChange={() => handleEventTypeToggle('appointment')}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">Citas</span>
        </label>
        <label className="inline-flex items-center gap-1.5 cursor-pointer">
          <input
            type="checkbox"
            checked={selectedEventTypes.includes('call')}
            onChange={() => handleEventTypeToggle('call')}
            className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
          />
          <span className="text-sm text-gray-700">Llamadas</span>
        </label>
        <label className="inline-flex items-center gap-1.5 cursor-pointer">
          <input
            type="checkbox"
            checked={selectedEventTypes.includes('task')}
            onChange={() => handleEventTypeToggle('task')}
            className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
          />
          <span className="text-sm text-gray-700">Tareas</span>
        </label>
      </div>
    </div>
  );
};
